import React from 'react';
import { injectIntl } from 'react-intl';
import { withApollo } from 'react-apollo';
import { withAuth } from '@cdk-prod/fortellis-auth-context';
import { LayoutGrid, LayoutGridCell } from '@cdk-uip/react-layout-grid';
import { withRouter } from 'react-router-dom';
import { Title } from '@cdk-uip/react-typography';
import { CardHeader, Card } from '@cdk-uip/react-card';
import { Button } from '@cdk-uip/react-button';
import cloneDeep from 'lodash/cloneDeep';
import { FormattedMessage } from 'react-intl';
import gql from 'graphql-tag';
import MySubscriptionListItemHeaderTitle from './my-subscription-list-header-title';
import MySubscriptionListItemHeaderLogo from './my-subscription-list-item-header-logo';
import MySubscriptionStatus from './my-subscription-status';
import SideNav from '../../../../solution-submission/common-sidenav-api/SideNavComponent/SideNav';
import MenuDataConfig from './menu-config';
import SolutionLogo from '../../../../common/images/cogs.png';
import Spinner from '../../../../common/Spinner';
import EntityDetails from '../../../../common/EntityDetails';

import moment from 'moment';
import SubscriptionPlanDetails from './SubscriptionPlanDetails';
import SubscriptionApiConfiguration from './SubscriptionApiConfiguration';
import SubscriptionManagementBillingDetails from './subscription-management-billing-details';
import FortellisDialog from '../../../../common/FortellisDialog';
import ActionSnackBar from '../../../../../components/solution-submission/ActionSnackBar';
import { sendAmplitudeData } from '../../../../../utils/amplitude';
import {
  AMPLITUDE_EVENTS,
  AMPLITUDE_PROPERTIES
} from '../../../../../utils/amplitude-constants';

const subscriptionFromDBQuery = gql`
  query($solutionId: ID, $entityId: ID) {
    getSolutionSubscriptions(solutionId: $solutionId, entityId: $entityId) {
      id
      subscriptionId
      solutionId
      entityId
      status
      isMarketPlaceSubScription
      plan {
        id
        isTheFreeTrial
        isFreeTrialEnabled
        position
        planName
        planDescription
        freeTrialPeriod
        freeTrialPeriodType
        billingType
        transactionalTypeOptions
        includedTransactionalType
        includedTransactionalFee
        billingFrequency
        basePrice
        includedUnitOfMeasure
        includedUnitQuantity
        additionalUnitPrice
        additionalUnitOfMeasure
        features {
          id
          title
          description
          featureImage
        }
      }
      subscriptionStartDate
      solution {
        id
        activateOnly
        overview {
          name
          publisherName
          solutionLogo
        }
      }
    }
  }
`;

const entityFromPlatform = gql`
  query($id: ID) {
    entitiesFromPlatform(id: $id) {
      id
      website
      lastUpdated
      created
      address {
        region
        city
        street
        countryCode
        postalCode
      }
      phone
      name
      type
    }
  }
`;

const subscriptionFromPlatformQuery = gql`
  query($subscriptionId: ID) {
    getSubscription(subscriptionId: $subscriptionId) {
      subscriptionId
      entityId
      solutionId
      status
      activated
      connections {
        api
        solutionId
        connectionId
        providerId
        subscriptionId
        entityId
        status
      }
    }
  }
`;

const cancelSubscriptionQuery = gql`
  mutation(
    $solutionId: String!
    $entityId: String!
    $subscriptionId: String
    $isBulkSubscribed: Boolean
  ) {
    appDeveloperCancellation(
      command: {
        solutionId: $solutionId
        entityId: $entityId
        subscriptionId: $subscriptionId
        isBulkSubscribed: $isBulkSubscribed
      }
    )
  }
`;

const getAsyncApiData = gql`
  query($solutionId: ID, $entityId: ID) {
    getAsyncConnections(solutionId: $solutionId, entityId: $entityId) {
      solutionId
      entityId
      api
      apiDisplayName
      apiProviderId
      apiType
      consentId
      consentType
      id
      subscriptionId
      status
      apiProviderName
    }
  }
`;

const solutionDetails = gql`
  query($solutionId: ID) {
    solutionDetails(id: $solutionId) {
      activateOnly
      overview {
        name
        solutionLogo
        publisherName
      }
    }
  }
`;

class SubscriptionManagement extends React.Component {
  state = {
    menu: MenuDataConfig.MenuData || {},
    platformSubscription: null,
    dbSubscription: null,
    entity: null,
    openCancelSubscriptionDialog: false,
    subscriptionActivationLoading: false,
    showSnackBar: false,
    cancellingSubscription: false,
    solutionDetails: null
  };

  handleMenuStateChange = newMenuState => {
    let menuNew = cloneDeep(newMenuState);
    this.setState({ menu: menuNew });
  };

  fetchSubscriptionFromDB(solutionId, entityId) {
    this.props.client
      .query({
        query: subscriptionFromDBQuery,
        variables: { entityId: entityId, solutionId: solutionId }
      })
      .then(response => {
        if (
          !response.errors &&
          response.data.getSolutionSubscriptions &&
          response.data.getSolutionSubscriptions.length
        ) {
          this.setState({
            dbSubscription: response.data.getSolutionSubscriptions[0]
          });
        }
      })
      .catch(error => {
        this.setState({
          error: 'Problem while fetching subscription from marketplace'
        });
      });
  }
  fetchEntityFromPlatform(entityId) {
    this.props.client
      .query({
        query: entityFromPlatform,
        variables: { id: entityId }
      })
      .then(response => {
        if (!response.errors && response.data.entitiesFromPlatform) {
          this.setState({ entity: response.data.entitiesFromPlatform });
        }
      })
      .catch(error => {
        this.setState({
          error: 'Entity Not found in platform'
        });
      });
  }
  fetchSolutionInfo(solutionId) {
    this.props.client
      .query({
        query: solutionDetails,
        variables: {
          solutionId: solutionId
        }
      })
      .then(response => {
        if (!response.errors && response?.data?.solutionDetails) {
          this.setState({
            solutionDetails: response.data.solutionDetails.splice(-1)[0]
          });
        }
      });
  }

  async fetchAsyncApi(solutionId, entityId) {
    return this.props.client.query({
      query: getAsyncApiData,
      variables: { solutionId: solutionId, entityId: entityId }
    });
  }

  async fetchData(subscriptionId, solutionId) {
    let response = await this.getSubscriptionFromPlatform(subscriptionId);

    this.fetchSolutionInfo(solutionId);

    if (response.errors || !response.data || !response.data.getSubscription) {
      this.setState({ error: 'Subscription Not found in platform' });
      return;
    }
    let allConnections = await this.fetchAsyncApi(
      solutionId,
      response.data.getSubscription.entityId
    );
    let asyncConnections = allConnections.data.getAsyncConnections.filter(
      item => item.apiType === 'async-api'
    );
    asyncConnections.forEach(element => {
      if (element.status === 'disabled') {
        element.status = 'inactive';
      } else if (element.status === 'enabled') {
        element.status = 'active';
      }
    });
    response.data.getSubscription.connections = response.data.getSubscription.connections.concat(
      asyncConnections
    );

    this.fetchSubscriptionFromDB(
      solutionId,
      response.data.getSubscription.entityId
    );
    this.fetchEntityFromPlatform(response.data.getSubscription.entityId);
    this.setState({
      platformSubscription: response.data.getSubscription
    });
  }

  getSubscriptionOverallStatus() {
    return (
      this.state.dbSubscription?.status ||
      this.state.platformSubscription?.status
    );
  }

  componentDidMount() {
    if (
      this.props.match &&
      this.props.match.params &&
      this.props.match.params.subscriptionId &&
      this.props.match.params.solutionId
    ) {
      this.setState({ error: null });
      this.fetchData(
        this.props.match.params.subscriptionId,
        this.props.match.params.solutionId
      );
    } else {
      this.setState({ error: 'SubscriptionId or solutionId not found in URL' });
    }
  }

  getSubscriptionFromPlatform = async subscriptionId => {
    return await this.props.client.query({
      query: subscriptionFromPlatformQuery,
      variables: { subscriptionId: subscriptionId }
    });
  };

  handleToggleChange = expandedMenuId => {
    this.setState({
      menu: Object.assign({}, this.state.menu, { expandedMenuId })
    });
  };

  calculateBillingEndDate() {
    if (!this.state.dbSubscription || !this.state.dbSubscription?.plan) {
      return null;
    }
    let unitMappingwithDaysValues = {
      Day: 1,
      Days: 1,
      Month: 30,
      Week: 8,
      Year: 365
    };
    let plan = this.state.dbSubscription?.plan;
    if (!plan.isTheFreeTrial) {
      return null;
    }
    let validityInDays = parseInt(
      '' +
        parseFloat('' + this.state.dbSubscription?.plan['freeTrialPeriod']) *
          parseFloat(
            unitMappingwithDaysValues[
              this.state.dbSubscription?.plan['freeTrialPeriodType'] ||
                unitMappingwithDaysValues['Year']
            ]
          ),
      10
    );
    return moment().add('days', validityInDays);
  }

  onCancelSubscription = () => {
    this.setState({ openCancelSubscriptionDialog: true });
  };

  getCancelSubscriptionDialog = () => {
    const { client, match, history, location, auth } = this.props;

    const { dbSubscription, entity, platformSubscription } = this.state;

    const { solutionId, entityId, subscriptionId } = match.params;

    return (
      <FortellisDialog
        message={`Are you sure you want to cancel ${
          this.state.solutionDetails.activateOnly === false
            ? 'subscription'
            : 'activation'
        }?`}
        open={this.state.openCancelSubscriptionDialog}
        acceptButtonName={
          <FormattedMessage
            id="CancelSubscription.accept"
            defaultMessage="Yes"
          />
        }
        cancelButtonName={
          <FormattedMessage
            id="CancelSubscription.cancel"
            defaultMessage="No"
          />
        }
        onAccept={async () => {
          const isBulkSubscribed = !Boolean(
            dbSubscription?.isMarketPlaceSubScription
          );

          try {
            this.setState({
              openCancelSubscriptionDialog: false,
              cancellingSubscription: true
            });

            await client.mutate({
              mutation: cancelSubscriptionQuery,
              variables: {
                solutionId,
                entityId,
                subscriptionId,
                isBulkSubscribed
              }
            });

            history.replace({
              pathname: location.pathname.split('/subscriptions')[0]
            });

            const amplitudeData = {
              [AMPLITUDE_EVENTS.CANCEL_ACTIVATION]: {
                [AMPLITUDE_PROPERTIES.ENTITY_ID]: entity?.id,
                [AMPLITUDE_PROPERTIES.ENTITY_NAME]: entity?.name,
                [AMPLITUDE_PROPERTIES.APP_ID]: platformSubscription?.solutionId,
                [AMPLITUDE_PROPERTIES.APP_NAME]: solutionDetails?.overview.name,
                [AMPLITUDE_PROPERTIES.SUBSCRIPTION_ID]: this?.planSubscription
                  ?.subscriptionId,
                [AMPLITUDE_PROPERTIES.ORG_ID]: auth?.entityId
              }
            };

            sendAmplitudeData(amplitudeData);
          } catch (err) {
            this.setState({
              openCancelSubscriptionDialog: false,
              cancellingSubscription: false,
              showSnackBar: true
            });
          }
        }}
        onCancel={() => {
          this.setState({ openCancelSubscriptionDialog: false });
        }}
        acceptDataCy={'btn_dialog_cancel_Subscription_ok'}
        cancelDataCy={'btn_dialog_cancel_Subscription_cancel'}
      />
    );
  };

  render() {
    if (this.state.error) {
      return (
        <div className="component-content">
          <LayoutGrid className="max-width-resp fm-solution-form-content">
            <LayoutGridCell span={3} className="fm-side-nav">
              <FormattedMessage
                id="Common.InternalServerError"
                defaultMessage="Internal Server Error 500"
              />
            </LayoutGridCell>
          </LayoutGrid>
        </div>
      );
    }
    if (
      !this.state.dbSubscription ||
      !this.state.platformSubscription ||
      !this.state.solutionDetails
    ) {
      return (
        <div className="component-content">
          <LayoutGrid className="max-width-resp fm-solution-form-content">
            <LayoutGridCell span={12} className="fm-side-nav">
              <Spinner />
            </LayoutGridCell>
          </LayoutGrid>
        </div>
      );
    }

    const {
      solutionDetails: solution,
      dbSubscription,
      platformSubscription
    } = this.state;

    if (
      solution.activateOnly !== false &&
      this.state.menu.contents[0].title === 'Subscription Details'
    ) {
      let newManu = cloneDeep(this.state.menu);
      newManu.contents[0].title = 'Activation Details';
      this.setState({ menu: newManu });
    }

    return (
      <div className="component-content">
        <div className="max-width-resp page-padding-common">
          <LayoutGrid className="fm-solution-form-content">
            <LayoutGridCell span={3} className="fm-side-nav">
              <SideNav
                sideNav={this.state.menu}
                handleToggleChange={this.handleToggleChange}
                handleSideNavStateChange={this.handleMenuStateChange}
              />
            </LayoutGridCell>
            <LayoutGridCell span={9}>
              <LayoutGrid className="fm-form-layout-grid">
                <LayoutGridCell span={12} className="form-box">
                  {this.state.menu.activeMenuId === 'overview' ? (
                    <div>
                      <div className="subscription-management-form-title">
                        <Title className="fm-page-content-title fm-page-solution-submission-title">
                          <FormattedMessage
                            id="SubscriptionManagement.overview"
                            defaultMessage="Overview"
                          />
                        </Title>
                      </div>

                      <div className="subscription-entity">
                        <EntityDetails entity={this.state.entity} />
                      </div>

                      <Card className="subscription-mamagement-card">
                        <CardHeader className="subscription-card-header">
                          <div className="subscription-card-header-content">
                            <div style={{ display: 'inline-flex' }}>
                              <MySubscriptionListItemHeaderLogo
                                image={
                                  solution.overview.solutionLogo || SolutionLogo
                                }
                              />
                            </div>
                            <div className="subscription-page-card-title-div">
                              <MySubscriptionListItemHeaderTitle
                                name={solution.overview.name}
                                dateOfPurchase={
                                  dbSubscription?.subscriptionCreatedOn ||
                                  platformSubscription?.activated
                                }
                                subscriptionId={
                                  dbSubscription?.subscriptionId ||
                                  platformSubscription?.subscriptionId
                                }
                                activateOnly={solution.activateOnly}
                              />
                            </div>
                            {this.getSubscriptionOverallStatus().toLowerCase() ===
                              'active' && (
                              <div className="cancel-subscription-button">
                                <Button
                                  primary
                                  raised
                                  onClick={this.onCancelSubscription}
                                >
                                  {solution.activateOnly === false
                                    ? 'Cancel Subscription'
                                    : 'Cancel Activation'}
                                </Button>
                              </div>
                            )}
                            {this.getCancelSubscriptionDialog()}
                            <div className="pull-right subscription-entity-expansion-panel-header-status">
                              <MySubscriptionStatus
                                status={this.getSubscriptionOverallStatus()}
                              />
                            </div>
                          </div>
                        </CardHeader>
                        <div className="subscription-management-card-body">
                          <div>
                            {solution.activateOnly === false &&
                            this.getSubscriptionOverallStatus().toLowerCase() ===
                              'active' &&
                            dbSubscription ? (
                              <SubscriptionManagementBillingDetails
                                plan={dbSubscription?.plan || null}
                                billingEndsOn={dbSubscription?.billingEndsOn}
                                billingStartsOn={
                                  dbSubscription?.billingStartsOn
                                }
                              />
                            ) : null}
                          </div>
                        </div>
                      </Card>
                      {solution.activateOnly === false &&
                      dbSubscription?.plan ? (
                        <div className="subscription-management-plan-details">
                          <div className="subscription-management-plan-details-header">
                            <FormattedMessage
                              id="SubscriptionManagement.PlanDetails.title"
                              defaultMessage="Plan Details"
                            />
                          </div>
                          <SubscriptionPlanDetails
                            plan={this.state.dbSubscription?.plan || null}
                          />
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {this.state.menu.activeMenuId === 'api_configuration' ? (
                    <SubscriptionApiConfiguration
                      platformSubscription={this.state.platformSubscription}
                      dbSubscription={this.state.dbSubscription}
                      entity={this.state.entity}
                      subscriptionStatus={this.getSubscriptionOverallStatus()}
                      solution={this.state.solutionDetails}
                    />
                  ) : null}
                </LayoutGridCell>
              </LayoutGrid>
            </LayoutGridCell>
          </LayoutGrid>
        </div>
        <ActionSnackBar
          show={!!this.state.showSnackBar}
          message={`Failed to cancel the  ${
            solution.activateOnly === false ? 'subscription' : 'activation'
          }. Please try again!!`}
          close={() => {
            this.setState({ showSnackBar: false });
          }}
        />
        <FortellisDialog
          open={this.state.cancellingSubscription}
          message={<Spinner />}
          hideFooter={true}
        />
      </div>
    );
  }
}

export default injectIntl(
  withAuth(withApollo(withRouter(SubscriptionManagement)))
);
